import { Avatar, Typography, Stack, Chip } from '@mui/material';
import { MRT_Cell, MRT_RowData } from 'material-react-table';
import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { removeUnderscore } from 'utils/string-utilis';

type ImageCellProps<T extends MRT_RowData> = {
    cell: MRT_Cell<T>;
    isLink?: boolean;
    defaultImage?: string;
    showImage?: boolean;
    target?: string;
    path?: string;
    cellWidth?: number;
};

const ImageCell = memo(<T extends MRT_RowData>(props: ImageCellProps<T>) => {
    const { cell, isLink = false, defaultImage = '/web.png', showImage = true, target = '_blank', path = '/' } = props;

    const columnName = cell.getValue<string>();
    const imageUrl = cell.row.original['imageUrl'] || defaultImage;
    const sxprops = {
        maxWidth: cell.column.getSize(),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };

    return (
        <Stack flexDirection="row" alignItems="center">
            {showImage && <Avatar src={imageUrl} alt={columnName} sx={{ width: 50, height: 50, mr: 2 }} />}
            {isLink ? (
                <Link to={path} target={target}>
                    <Typography noWrap sx={sxprops}>
                        {columnName}
                    </Typography>
                </Link>
            ) : (
                <Typography noWrap sx={sxprops}>
                    {columnName}
                </Typography>
            )}
        </Stack>
    );
}) as <T extends MRT_RowData>(props: ImageCellProps<T>) => JSX.Element;

type ChipCellProps = {
    value: string;
    translationKey?: string;
    label?: string;
    statusColorMap:
        | Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'>
        | string;
    icon?: JSX.Element;
};

export const ChipCell: FC<ChipCellProps> = memo(({ value, statusColorMap, label, icon, translationKey }) => {
    const { t } = useTranslation();

    return (
        <Chip
            label={label || removeUnderscore(t(`${translationKey}.${value}`))}
            color={statusColorMap[value] ?? 'default'}
            variant="outlined"
            size="small"
            icon={icon}
        />
    );
});

export default ImageCell;
